import { createBrowserRouter, Params } from "react-router-dom";
import store from "./store";
import {
  rapidstorApi,
  selectLocationBySlug,
} from "./features/rapidstor/rapidstorApiSlice";
import Root from "./routes/Root";
import Error from "./routes/Error";
import LocationList from "./routes/LocationList";
import QuestionnairePage from "./routes/QuestionnairePage";
import React from "react";
import RapidStorPage from "./routes/RapidStorPage";
import ContactPage from "./routes/ContactPage";
import UnitListing from "./routes/UnitListing";
import BookUnit from "./routes/BookUnit";
import { goBackOnePage } from "./store/questionnaireSlice";
import Location from "./features/rapidstor/entities/Location";
import { Page } from "@r6digital/rapidstor-types/types/Questionnaire";
import QuoteSent from "./routes/QuoteSent";
import BookConfirmed from "./routes/BookConfirmed";
import "./App.scss";
import { options } from "./services/rapidstor";

interface LocationParams {
  locationSlug: string;
}

interface OptionalLocationParams {
  locationSlug?: string;
}

export const siteUrl = options.enabledLocations.includes("L005")
  ? "https://welshpoolselfstorage.com.au/"
  : "https://keepsafestorage.com.au/";

type Destination = string | number | (() => void);

/**
 * Get the destination to go back to when the back button is pressed.
 *
 * Returns one of the following:
 * string: a URL to navigate to
 * number: relative movement through browser history stack
 * function: a function to call
 *
 * @param path
 * @param location
 * @param questionnaireStack
 */
export function getBackDestination(
  path: string,
  location: Location | null,
  questionnaireStack?: Page[]
): Destination {
  if (!location) {
    // Location will only be falsey if the user had just loaded the booking system for the first time
    return () => window.location.assign(siteUrl);
  }

  if (path === "/booking") {
    return `/locations/${location.slug}`;
  } else if (path.match(/^\/locations\/[^/]+\/?$/)) {
    // When in the questionnaire, go back through the steps instead of navigating back
    // immediately. The stack length must be at least 2, because the last element in the
    // stack is the current step.
    if (questionnaireStack && questionnaireStack.length >= 2) {
      return () => store.dispatch(goBackOnePage());
    } else {
      return "/";
    }
  } else if (path.match(/^\/locations\/[^/]+\/units\/?$/)) {
    // When in the unit listing, go back to the questionnaire
    return `/locations/${location.slug}`;
  } else if (path.match(/^\/locations\/[^/]+\/(book|reserve)\/?$/)) {
    return `/locations/${location.slug}/units`;
  } else if (
    path === "/contact" ||
    path.match(/\/quote-sent\/?$/) ||
    path.match(/^\/contact\/[^/]+\/?$/)
  ) {
    // Treat the contact page as almost a separate drawer. Closing the contact page will
    // always go back to what you were looking at before you opened it, and using the
    // go back button will never return you to the contact page.
    return -1;
  } else if (path === "/") {
    return () => window.location.assign(siteUrl);
  } else {
    return "/";
  }
}

const locationLoader = async ({ params }: { params: Params<keyof LocationParams> }) => {
  const promise = store.dispatch(rapidstorApi.endpoints.getLocations.initiate());
  try {
    await promise.unwrap();
    const location = selectLocationBySlug(
      store.getState(),
      params.locationSlug as string
    );

    if (!location) {
      throw new Response("", {
        status: 404,
        statusText: "Not Found",
      });
    }

    return location;
  } finally {
    promise.unsubscribe();
  }
};

const optionalLocationLoader = async ({
  params,
}: {
  params: Params<keyof OptionalLocationParams>;
}) => {
  if (params.locationSlug) {
    return await locationLoader({ params });
  }

  return null;
};

export default createBrowserRouter(
  [
    {
      path: "/",
      element: <Root />,
      errorElement: <Error />,
      children: [
        {
          path: "",
          element: <LocationList />,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug",
          element: <QuestionnairePage />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/units",
          element: <UnitListing />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/reserve",
          element: <BookUnit />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/book",
          element: <BookUnit />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/book-confirmed",
          element: <BookConfirmed />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/quote-sent",
          element: <QuoteSent />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "locations/:locationSlug/booking",
          element: <RapidStorPage />,
          loader: locationLoader,
          errorElement: <Error />,
        },
        {
          path: "contact/:locationSlug?",
          element: <ContactPage />,
          loader: optionalLocationLoader,
        },
      ],
    },
  ],
  {
    basename: import.meta.env.PROD ? "/book-a-unit" : "/",
  }
);
